<template>
    <div class="orders-page">
        <slot name="breadcrumbs">
            <breadcrumbs v-if="!isMobile" class="mb-8" />
            <page-title :text="$t('page_titles.orders_page_title')" />
        </slot>
        <teleport v-if="isMobile && !companyPageMode" to="#under-header">
            <v-tabs v-model="selectedTab" :stacked="isMobile" fixed-tabs class="sticky-bar">
                <v-tab v-for="tab in tabs" :key="tab.label">
                    {{ isMobile ? $t(`${tab.label}`) : '' }}
                    <v-icon>{{ tab.icon }}</v-icon>
                </v-tab>
            </v-tabs>
        </teleport>
        <v-row dense class="mt-8 mt-md-0">
            <v-col
                v-if="(!isMobile || selectedTab === 0) && !isSupplier && personRole !== 'dispatcher'"
                class="orders-page__book-col"
                cols="12"
                sm="4"
                md="3"
            >
                <slot name="bookButtons" :new-order-request-modal="newOrderRequestModal">
                    <v-btn class="bg-primary" size="small" to="/">
                        {{ $t('new_order') }}
                    </v-btn>
                    <v-btn
                        v-if="isShowNewReservationRequest"
                        size="small"
                        class="bg-grey-lighten-4 ms-2"
                        @click="newOrderRequestModal = true"
                    >
                        {{ $t('new_reservation_request') }}
                    </v-btn>
                </slot>
                <new-order-request-modal ref="newOrderRequestModal" v-model="newOrderRequestModal" />
            </v-col>
            <!-- Quick Filter hidden for ZSH -->
        </v-row>
        <div v-show="!isMobile || selectedTab === 1">
            <order-selected-filters v-if="!companyPageMode" @filter="filterOrderSelectedFilters">
                <template v-slot:save-btn>
                    <custom-filter-modal
                        v-if="isSomeFiltersSelected"
                        :filters="ordersFilters"
                        :person="person"
                        @create-custom-filter="$refs.quickFilter?.loadCustomFilters()"
                    />
                </template>
            </order-selected-filters>
            <order-filters
                ref="filters"
                :filters-list="filtersList"
                :hidden-filters="hiddenFilters"
                :company-page-mode="companyPageMode"
                :product-page-mode="productPageMode"
                @change-filters="hiddenFilters = {...hiddenFilters, filters: $event}"
                @filter="filterOrderFilters"
                @reset-filters="onFiltersReset"
            />
        </div>
        <div v-show="!isMobile || selectedTab === 0">
            <div v-if="!isMobile" class="orders-page__pre-table">
                <v-divider />
                <div class="d-flex align-center" style="min-height: 40px">
                    <div class="d-flex align-center">
                        <div v-if="ordersCount" class="me-2 text-grey text-caption d-flex flex-wrap">
                            <span class="nowrap">
                                {{ $t('orders_count') }}:
                                <strong>{{ ordersCount }}</strong>
                            </span>
                            <span class="nowrap ms-2">
                                {{ $t('services_count') }}:
                                <strong>{{ servicesCount }}</strong>
                            </span>
                        </div>
                        <slot name="agentsPaymentsUpload">
                            <agents-payments-upload v-if="isTO1 && !isSupplier" />
                        </slot>
                    </div>
                    <v-spacer />
                    <div class="d-flex align-center">
                        <show-commission-and-netto-checkbox v-if="!isSupplier" />
                        <cancelled-services-checkbox v-if="!isSupplier" />
                        <reservations-table-options-modal
                            :table-columns="tableColumns"
                            class="me-2"
                            @change="hiddenColumns.columns = $event"
                        />
                        <v-btn-toggle
                            v-model="expandedAllButtonGroup"
                            class="ms-2"
                            density="compact"
                            color="primary"
                            mandatory
                        >
                            <v-btn :value="0" size="small">
                                <v-icon>mdi-unfold-more-horizontal</v-icon>
                            </v-btn>
                            <v-btn :value="1" size="small">
                                <v-icon>mdi-unfold-less-horizontal</v-icon>
                            </v-btn>
                        </v-btn-toggle>
                    </div>
                </div>
            </div>
            <v-row dense>
                <v-col cols="12">
                    <orders
                        ref="orders"
                        v-model="selectedOrders"
                        :hidden-columns="hiddenColumns"
                        :company-page-mode="companyPageMode"
                        :product-page-mode="productPageMode"
                        :is-expanded-all="!expandedAllButtonGroup"
                        :selectable="selectable"
                        :table-columns="tableColumns"
                        @change-sort-order="sortOrder = $event"
                    />
                </v-col>
            </v-row>
        </div>
        <assistant-chat
            v-if="$conf.public.openAI.showAssistant"
            :assistant-id="$conf.public.openAI.ordersPageAssistantKey"
            :instructions="assistantInstructions"
            page="orders"
        />
    </div>
</template>

<script lang="ts">
    import {Component, toNative} from 'vue-facing-decorator'
    import __OrdersPage, {OrdersPage as _OrdersPage} from '~/pages/account/OrdersPage.vue'

    @Component({mixins: [__OrdersPage]})
    export class OrdersPage extends _OrdersPage {
        // Override tableColumns to hide Invoice and Voucher columns by default for ZSH
        get tableColumns() {
            const tableColumns = super.tableColumns

            // For ZSH, hide Invoice and Voucher columns by default (when no user-specific settings exist)
            if (this.isDefaultTableColumns) {
                tableColumns.forEach(column => {
                    if (column.value === 'invoice' || column.value === 'voucher') {
                        column.show = false
                    }
                })
            }

            return tableColumns
        }
    }

    export default {...toNative(OrdersPage), ssrRender: __OrdersPage.ssrRender}
</script>
